/**
 * brandedConfirmAlert
 *
 * Drop-in replacement for react-confirm-alert's `confirmAlert()` — same
 * { title, message, buttons } shape every existing call site already uses —
 * rendered through PopupShell (the same on-brand chrome as the subscription
 * popup and the coach onboarding popup) instead of the library's default
 * dialog, which was never themed: nobody ever imported
 * 'react-confirm-alert/src/react-confirm-alert.css' or customized it, so
 * every confirmAlert() in the app rendered a plain white Arial box with
 * grey (#333) buttons — the only unbranded popups left.
 *
 * Still uses react-confirm-alert as the imperative mount/unmount plumbing
 * (it renders into a detached div appended to document.body via
 * ReactDOM.render — see node_modules/react-confirm-alert/lib/index.js),
 * which matters for callers with no React tree of their own, e.g. the
 * redux thunks in socket.duck.js. Only the rendered UI changes, via
 * react-confirm-alert's `customUI` render-prop escape hatch.
 */
import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import PopupShell from './PopupShell';
import css from './PopupShell.css';

// react-confirm-alert always wraps customUI in its own
// `.react-confirm-alert-overlay` (unbranded white 90%-opacity backdrop —
// see public/static/css/popup-styles.css). PopupShell renders its own
// full-viewport on-brand overlay on top of that, so the outer one would
// otherwise show through PopupShell's semi-transparent backdrop as a pale
// wash behind the intended dark blur. This class neutralizes it, keeping
// the outer div purely as mount plumbing.
const OVERLAY_RESET_CLASS = css.brandedConfirmOverlayReset;

const TONE_BUTTON_CLASS = {
  success: css.popupShellBtnGold,
  warning: css.popupShellBtnGold,
  danger: css.popupShellBtnDanger,
  default: css.popupShellBtnPrimary,
};

// Icon wrap only has a distinct look for 'danger' so far (see
// popupShellIconWrapDanger) — other tones fall back to PopupShell's default
// violet gradient.
const TONE_ICON_VARIANT = {
  danger: 'danger',
};

/**
 * tone picks the primary (first) button's color:
 *   'success' / 'warning' — gold, matching the subscription popup's CTA
 *   'danger'              — red, for real failures
 *   'default'             — violet/marketplace-color gradient (default)
 * Any additional buttons render as secondary (outlined/text) actions.
 */
export function brandedConfirmAlert({ title, message, buttons, tone = 'default', icon }) {
  const safeButtons = buttons && buttons.length > 0 ? buttons : [{ label: 'Ok' }];
  const primaryClass = TONE_BUTTON_CLASS[tone] || TONE_BUTTON_CLASS.default;
  const iconVariant = TONE_ICON_VARIANT[tone];

  confirmAlert({
    // PopupShell owns Escape / click-outside itself (see its useEffect and
    // overlay onClick) and calls the same onClose react-confirm-alert hands
    // to customUI — leaving these enabled here would fire react-confirm-
    // alert's own close() a second time on the same keypress/click, which
    // throws (it removes a DOM node that's already gone).
    closeOnEscape: false,
    closeOnClickOutside: false,
    overlayClassName: OVERLAY_RESET_CLASS,
    customUI: ({ onClose }) => (
      <PopupShell
        onClose={onClose}
        // An icon warrants the full glow header (more visual weight); plain
        // title-only alerts stay on the compact bar as before.
        headerVariant={icon ? 'full' : 'compact'}
        icon={icon}
        iconVariant={iconVariant}
        title={title}
        maxWidth={420}
        footer={
          <div className={css.popupShellNavRow}>
            {safeButtons.map((button, i) => (
              <button
                key={i}
                type="button"
                className={`${css.popupShellBtn} ${i === 0 ? primaryClass : css.popupShellBtnSecondary}`}
                onClick={() => {
                  if (button.onClick) button.onClick();
                  onClose();
                }}
              >
                {button.label}
              </button>
            ))}
          </div>
        }
      >
        {message && <p className={css.popupShellBodyText}>{message}</p>}
      </PopupShell>
    ),
  });
}
